/* aviaf — Research (CR) tab: node health, pipelines/lanes, runs feed, candidate funnel, ask-the-researcher */
(() => {
const { useState } = React;
const S = () => window.OpsStore;

function TickCountdown() {
  window.useTicker(1000);
  const t = window.nextTickUtc() - Date.now();
  const h = Math.floor(t / 3600000), m = Math.floor((t % 3600000) / 60000), s = Math.floor((t % 60000) / 1000);
  return <span className="tnum">{h}h {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s</span>;
}

function Switch({ on, onChange, label }) {
  return <button className="osw" data-on={on} onClick={() => onChange(!on)} title={label}><span className="osw-knob" /></button>;
}

/* ── node header strip ── */
function NodeStrip() {
  useOps(); window.useTicker(20000);
  const node = S().node || {};
  const st = node.status, pol = node.policy;
  // Honest empty state: no policy row means the API returned nothing (or the
  // hub is unreachable) — never fabricate a node identity.
  if (!pol) return (
    <div className="node-strip">
      <div className="ns-cell">
        <div className="lbl">node · aviaf-claude</div>
        <div className="val mono muted">policy not loaded</div>
        <div className="sub mono">GET /api/node/policy returned nothing</div>
      </div>
    </div>
  );
  const hasStatus = !!(st && st.ts != null);
  const detail = (hasStatus && st.detail) || {};
  const repoAge = hasStatus ? st.repo_age_days : null;
  const repoState = repoAge == null ? "" : repoAge > 30 ? "neg" : repoAge > 7 ? "warn" : "ok";
  const online = hasStatus && (Date.now() - st.ts < 2 * 3600000);
  return (
    <div className="node-strip">
      <div className="ns-cell">
        <div className="lbl">node · aviaf-claude</div>
        <div className="val"><span className={"inc-dot " + (online ? "ok" : "warn")} />{hasStatus ? (online ? "reporting" : "quiet") : "no report yet"}</div>
        <div className="sub mono">{hasStatus ? <>last report <window.Age ts={st.ts} suffix=" ago" /> · tick-based, not resident</> : "node has not POSTed /api/node/status"}</div>
      </div>
      <div className="ns-cell">
        <div className="lbl">repo freshness</div>
        <div className={"val mono " + repoState}>{hasStatus ? st.repo_sha : "—"} {repoAge != null && <span style={{ fontSize: 12 }}>· {repoAge}d old</span>}</div>
        <div className="sub mono">amber &gt;7d · red &gt;30d (a 89d-stale checkout once nulled research)</div>
      </div>
      <div className="ns-cell">
        <div className="lbl">tonight's tick · 21:15Z</div>
        <div className="val mono"><TickCountdown /></div>
        <div className="sub mono">last tick {hasStatus ? window.fmtTs(st.last_tick) : "—"} → <b className="ok">{hasStatus ? st.last_tick_result : "—"}</b></div>
      </div>
      <div className="ns-cell">
        <div className="lbl">sessions today</div>
        <div className="val mono">{hasStatus ? st.sessions_today : "—"} <span className="muted" style={{ fontSize: 13 }}>/ {pol.max_sessions_per_day}</span></div>
        <div className="sub mono">{window.fmtTok(detail.tokens_today)} tokens · KB {detail.kb_open_questions == null ? "—" : detail.kb_open_questions} open / {detail.kb_blocked_questions == null ? "—" : detail.kb_blocked_questions} blocked</div>
      </div>
      <div className="ns-cell">
        <div className="lbl">CR master breaker</div>
        <div className="val" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Switch on={!!pol.cr_enabled} onChange={(v) => S().setMaster("cr_enabled", v ? 1 : 0)} label="cr_enabled" />
          <span className={"mono " + (pol.cr_enabled ? "ok" : "neg")}>{pol.cr_enabled ? "ENABLED" : "DISABLED"}</span>
        </div>
        <div className="sub mono">journalled · node honors on next policy-sync</div>
      </div>
    </div>
  );
}

/* ── pipelines / lanes ── */
const PIPES = [
  { key: "cr-nightly", lane: "cr-nightly", desc: "kb-loop · one research iteration per night" },
  { key: "cr-deep", lane: "cr-nightly", desc: "multi-session strategic investigations", shared: true },
  { key: "on-demand", lane: "cr-nightly", desc: "operator questions · ride the next tick", shared: true },
  { key: "strategic", lane: "strategic", desc: "week review · program odds · kill-criteria" },
];
function Pipelines() {
  useOps();
  const crEnabled = !!(S().node.policy && S().node.policy.cr_enabled);
  return (
    <div className="lane-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
      {PIPES.map((p) => {
        const lane = S().lanes.find((l) => l.id === p.lane);
        const runs = S().runs.filter((r) => r.lane === p.key).slice(0, 5);
        if (!lane) return (
          <div key={p.key} className="lane-card" data-dead={true}>
            <div className="lc-h"><span className="lc-name mono">{p.key}</span></div>
            <div className="lc-desc">{p.desc}</div>
            <div className="mono muted" style={{ fontSize: 11, padding: "6px 0" }}>lane policy not loaded (GET /api/node/lanes)</div>
          </div>
        );
        const dead = !lane.enabled || lane.killed || !crEnabled;
        return (
          <div key={p.key} className="lane-card" data-dead={dead}>
            <div className="lc-h">
              <span className="lc-name mono">{p.key}</span>
              <window.ModelChip model={p.key === "cr-deep" ? "opus" : lane.model} note={p.key === "cr-nightly" ? lane.modelNote : p.key === "cr-deep" ? "upward override — armed by approval" : null} />
              {!p.shared && <Switch on={lane.enabled && !lane.killed} onChange={(v) => S().setLane(lane.id, { top: { enabled: v } }, `enable → ${v}`)} label={"enable " + lane.id} />}
            </div>
            <div className="lc-desc">{p.desc}</div>
            {p.shared ? (
              <div className="lc-shared mono">draws <b>{p.lane}</b> budget ↓</div>
            ) : (
              <div className="mono muted" style={{ fontSize: 10.5, padding: "4px 0", lineHeight: 1.5 }} title="the node does not report per-lane spend to the hub yet — budget shown, spend is an honest empty state">
                budget {lane.budget.sessions}/{lane.budget.sessionsUnit} · {window.fmtTok(lane.budget.tokens)}/{lane.budget.tokensUnit}
                <br />spend not yet reported by node
              </div>
            )}
            <div className="lc-runs">
              {runs.length === 0 ? <div className="mono muted" style={{ fontSize: 11, padding: "6px 0" }}>no runs yet</div> : runs.map((r) => (
                <div key={r.id} className="lc-run mono" title={r.id}>
                  <span className={"inc-dot " + (r.status === "running" ? "run" : "ok")} />
                  <span className="lc-run-id">{r.id.replace(/^(kb-loop|cr-deep|ondemand|strategic)-/, "")}</span>
                  <span className="muted">{r.finished ? window.fmtAge(r.finished) + " ago" : "running"}</span>
                </div>
              ))}
            </div>
            {p.key === "strategic" && <div className="lc-foot mono">output is ALWAYS proposals — never direct action</div>}
          </div>
        );
      })}
    </div>
  );
}

/* ── runs feed ── */
function RunRow({ r }) {
  const [open, setOpen] = useState(false);
  useOps();
  const dur = r.finished ? Math.round((r.finished - r.started) / 60000) + "m" : window.fmtAge(r.started) + " elapsed";
  const recs = (r.recs || []).map((pk) => S().actions.find((a) => a.pk === pk)).filter(Boolean);
  return (
    <div className="run-row" data-open={open} data-running={r.status === "running"}>
      <div className="run-l1" onClick={() => setOpen(!open)}>
        <span className={"inc-dot " + (r.status === "running" ? "run" : "ok")} />
        <span className="run-id mono">{r.id}</span>
        <span className="run-lane mono">{r.lane}</span>
        <window.ModelChip model={r.model} />
        <span className="mono muted run-dur">{dur}</span>
        <span className={"run-st mono " + (r.status === "running" ? "accent" : "muted")}>{r.status.toUpperCase()}</span>
      </div>
      <div className="run-head" onClick={() => setOpen(!open)}>{r.head}</div>
      {open && (
        <div className="run-detail">
          <p className="ss-prose" style={{ maxWidth: "76ch" }}>{r.summary}</p>
          {r.triggers && (
            <div className="run-trips">
              <span className="lbl mono">armed tripwires</span>
              {r.triggers.map((t) => (
                <span key={t.id} className="trip-chip" data-state={t.state}>[{t.id}] {t.text} — {t.state.toUpperCase()}</span>
              ))}
            </div>
          )}
          <div className="run-links mono">
            {r.metrics && <span className="muted">{r.metrics.sessions} session{r.metrics.sessions > 1 ? "s" : ""} · {window.fmtTok(r.metrics.tokens)} tok{r.metrics.questions_closed !== undefined ? ` · KB ${r.metrics.questions_closed} closed / ${r.metrics.questions_opened} opened` : ""}</span>}
            {(r.candidates || []).map((c) => <a key={c} href="#/research" onClick={(e) => e.preventDefault()} className="run-link" title="in funnel below">↳ {c}</a>)}
            {recs.map((a) => <a key={a.pk} href="#/ops" className="run-link" data-dq="true">→ decision #{a.pk} ({a.status})</a>)}
            {r.incident && <a className="run-link" href="#/ops" onClick={(e) => { e.preventDefault(); window.openIncidentDrawer(r.incident); }}>⚠ incident {r.incident}</a>}
            {r.request && <span className="muted">from research_request #{r.request}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function RunsFeed() {
  useOps();
  const [lane, setLane] = useState("all");
  const runs = S().runs.filter((r) => lane === "all" || r.lane === lane);
  return (
    <>
      <window.SectionHeader title="Runs" count={S().runs.length} sub="research_runs · newest first" right={
        <>{["all", "cr-nightly", "cr-deep", "on-demand", "strategic"].map((k) => <button key={k} data-active={lane === k} onClick={() => setLane(k)}>{k}</button>)}</>
      } />
      <div>{runs.map((r) => <RunRow key={r.id} r={r} />)}
        {runs.length === 0 && <div className="ops-empty mono">no runs in this lane</div>}
      </div>
    </>
  );
}

/* ── candidate funnel ── */
const FUNNEL_COLS = [
  { st: "proposed", label: "PROPOSED", note: "node may move ↔ shadow" },
  { st: "shadow", label: "SHADOW", note: "live signals, no real orders" },
  { st: "live-recommended", label: "LIVE-RECOMMENDED", note: "human act — via decision queue" },
  { st: "retired", label: "RETIRED", note: "failed observation / superseded" },
];
function CandCard({ c }) {
  useOps();
  const dq = c.decision ? S().actions.find((a) => a.pk === c.decision) : null;
  return (
    <div className="fun-card" data-perm={c.permanent}>
      <div className="fc-h">
        <span className="fc-strat mono">{c.strategy}</span>
        {c.permanent && <span className="perm-chip" title="governance lock for funded accounts">SHADOW-PERMANENT</span>}
        <span className="mono muted" style={{ marginLeft: "auto", fontSize: 10.5 }}><window.Age ts={c.statusSince} suffix=" in status" /></span>
      </div>
      <div className="fc-id mono">{c.id} · created {window.fmtTs(c.created)}</div>
      {c.obs ? (
        <div className="fc-obs">
          <div className="fc-stats mono">
            <span>n <b>{c.obs.trades}</b></span><span>PF <b className={c.obs.pf >= 1.2 ? "ok" : c.obs.pf < 1 ? "neg" : ""}>{c.obs.pf.toFixed(2)}</b></span>
            <span>E[R] <b>{c.obs.er.toFixed(2)}</b></span><span>worst <b className="neg">{window.fmt$(c.obs.worstDay)}</b></span>
          </div>
          {c.spark && <span style={{ color: c.spark.at(-1) >= 0 ? "var(--pos)" : "var(--neg)", display: "block" }}><window.Sparkline data={c.spark} height={22} /></span>}
        </div>
      ) : (
        <div className="fc-nodata mono">no observation data yet — shadow not started</div>
      )}
      <div className="fc-notes">{c.notes}</div>
      <div className="fc-actions">
        {c.status === "proposed" && !c.permanent && <>
          <window.Btn onClick={() => S().setCandidate(c.id, "shadow")}>→ shadow</window.Btn>
          <window.Btn onClick={() => S().setCandidate(c.id, "retired")}>retire</window.Btn>
        </>}
        {c.status === "shadow" && !c.permanent && <>
          {!dq || dq.status !== "proposed" ? <window.Btn kind="accent" title="creates a destructive-tier decision-queue item — nothing goes live from here" onClick={() => S().setCandidate(c.id, "live-recommended")}>propose live →</window.Btn> : null}
          <window.Btn onClick={() => S().setCandidate(c.id, "retired")}>retire</window.Btn>
        </>}
        {dq && dq.status === "proposed" && <a className="fc-dq mono" href="#/ops">decision #{dq.pk} awaiting operator →</a>}
        {c.status === "live-recommended" && (!dq || dq.status !== "proposed") && <span className="mono muted" style={{ fontSize: 10.5 }}>engine-side deploy is manual</span>}
      </div>
    </div>
  );
}
function Funnel() {
  useOps();
  return (
    <div className="fun-grid">
      {FUNNEL_COLS.map((col) => {
        const cards = S().candidates.filter((c) => c.status === col.st);
        return (
          <div key={col.st} className="fun-col" data-st={col.st}>
            <div className="fun-col-h mono">{col.label} <span className="tnum">{cards.length}</span><span className="fun-note">{col.note}</span></div>
            {cards.map((c) => <CandCard key={c.id} c={c} />)}
            {cards.length === 0 && <div className="ops-empty mono" style={{ padding: "14px 4px" }}>none</div>}
          </div>
        );
      })}
    </div>
  );
}

/* ── ask the researcher ── */
function AskBox() {
  useOps();
  const [q, setQ] = useState("");
  const pending = S().actions.filter((a) => a.kind === "research_request" && a.status === "proposed");
  return (
    <div className="ask-wrap">
      <div className="ask-row">
        <input className="ops-input" style={{ flex: 1 }} placeholder="ask the researcher — files a research_request the node picks up at its next tick (21:15Z)"
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) { S().fileResearchRequest(q); setQ(""); } }} />
        <window.Btn kind="primary" onClick={() => { if (q.trim()) { S().fileResearchRequest(q); setQ(""); } }}>File request</window.Btn>
      </div>
      {pending.length > 0 && (
        <div className="ask-pending mono">
          {pending.map((p) => <span key={p.pk}>queued · #{p.pk} “{p.args.question}” — next tick {window.fmtTs(window.nextTickUtc())} · <a href="#/ops" style={{ color: "var(--accent)" }}>in decision queue</a></span>)}
        </div>
      )}
      <div className="ask-hint mono">contract: techops_actions kind=research_request, status=proposed, args_json={"{question}"} — rides the node's policy poll</div>
    </div>
  );
}

window.PageResearch = function PageResearch() {
  useOps();
  const nCands = S().candidates.filter((c) => c.status !== "retired").length;
  return (
   <div className="ops-console">
    <div className="cr-page">
      <NodeStrip />
      <window.SectionHeader title="Pipelines" sub="lanes · budgets shared 1:1 with Ops — no second source of truth" right={<a className="mono" style={{ color: "var(--accent)", textDecoration: "none", fontSize: 11 }} href="#/ops">lane controls on Ops →</a>} />
      <Pipelines />
      <RunsFeed />
      <window.SectionHeader title="Candidate funnel" count={nCands} sub="strategy_candidates · promotion to live is always a human act" />
      <Funnel />
      <window.SectionHeader title="Ask the researcher" sub="research_request → next tick" />
      <AskBox />
    </div>
   </div>
  );
};
})();
